import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Card, CardContent, Typography, Box, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider, Stack, TextField, CircularProgress, Avatar } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SaveIcon from '@mui/icons-material/Save';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import StatusChip from '../../components/StatusChip';
import OrderCodes from '../../components/OrderCodes';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import { getOrderDetail, updateOrderStatus } from '../../api/endpoints/shop';

const statusColor = { pending: 'warning', confirmed: 'info', shipped: 'primary', delivered: 'success', cancelled: 'error', returned: 'default' };

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState('');

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const res = await getOrderDetail(id);
      setOrder(res.data.data);
      setTracking(res.data.data.trackingNumber || '');
    } catch {
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (status) => {
    try {
      await updateOrderStatus(id, status, tracking);
      toast.success('Order status updated');
      fetchOrder();
    } catch {
      toast.error('Failed to update order');
    }
  };

  const handleSaveTracking = async () => {
    try {
      await updateOrderStatus(id, order.orderStatus, tracking);
      toast.success('Tracking number saved');
      fetchOrder();
    } catch {
      toast.error('Failed to save tracking');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!order) return <Typography sx={{ p: 4 }}>Order not found</Typography>;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon sx={{ fontSize: 28 }} />} onClick={() => navigate('/shop/orders')} sx={{ mb: 2 }}>Back to Orders</Button>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>Order #{order.orderNumber}</Typography>
                  <Typography variant="caption" color="text.secondary">Placed on {formatDateTime(order.createdAt)}</Typography>
                </Box>
                <Chip label={order.orderStatus} size="small" color={statusColor[order.orderStatus] || 'default'} />
              </Box>
              {order.trackingNumber && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#F0F9FF', borderRadius: 1, border: '1px solid #BAE6FD', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalShippingIcon color="info" fontSize="small" />
                  <Typography variant="body2" fontWeight={500}>Tracking: {order.trackingNumber}</Typography>
                </Box>
              )}
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#FAFAFA', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <OrderCodes order={order} />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.items?.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {(item.image || item.productId?.images?.[0]) ? (
                              <Avatar src={item.image || item.productId?.images?.[0]} variant="rounded" sx={{ width: 40, height: 40 }} />
                            ) : null}
                            <Box>
                              <Typography variant="body2" fontWeight={500}>{item.productName || item.productId?.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {item.productId?._id || 'N/A'}
                                {item.productId?.category ? ` · ${item.productId.category}` : ''}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.price * item.quantity)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1} alignItems="flex-end">
                <Typography variant="body2">Subtotal: {formatCurrency(order.subtotal)}</Typography>
                {order.shippingCharge > 0 && <Typography variant="body2">Shipping: {formatCurrency(order.shippingCharge)}</Typography>}
                <Typography variant="body2">Tax: {formatCurrency(order.tax)}</Typography>
                {order.discount > 0 && <Typography variant="body2" color="error">Discount: -{formatCurrency(order.discount)}</Typography>}
                {order.couponCode && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: -0.5 }}>Coupon: {order.couponCode}</Typography>}
                <Typography variant="h6" fontWeight={700}>Total: {formatCurrency(order.total)}</Typography>
              </Stack>
              {order.notes && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#FFF8F0', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Order Notes</Typography>
                  <Typography variant="body2">{order.notes}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Customer</Typography>
              <Typography variant="body2" fontWeight={500}>{order.userId?.name || order.shippingAddress?.name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <EmailIcon fontSize="inherit" color="action" />
                <Typography variant="caption" color="text.secondary">{order.userId?.email || 'No email'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocalPhoneIcon fontSize="inherit" color="action" />
                <Typography variant="caption" color="text.secondary">{order.shippingAddress?.phone || order.userId?.phone}</Typography>
              </Box>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>User ID: {order.userId?._id}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Shipping Address</Typography>
              {order.shippingAddress ? (
                <>
                  <Typography variant="body2" fontWeight={500}>{order.shippingAddress.name}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{order.shippingAddress.phone}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{order.shippingAddress.line1}</Typography>
                  {order.shippingAddress.line2 && <Typography variant="body2">{order.shippingAddress.line2}</Typography>}
                  <Typography variant="body2">{order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}</Typography>
                </>
              ) : <Typography variant="body2" color="text.secondary">No address</Typography>}
            </CardContent>
          </Card>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Payment</Typography>
              <Typography variant="body2">Status: <Chip label={order.paymentStatus} size="small" color={order.paymentStatus === 'paid' ? 'success' : order.paymentStatus === 'failed' ? 'error' : 'warning'} /></Typography>
              {order.paymentId && <Typography variant="caption" display="block" color="text.secondary">Payment ID: {order.paymentId}</Typography>}
              {order.currency && <Typography variant="caption" display="block" color="text.secondary">Currency: {order.currency}</Typography>}
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Update Status</Typography>
              <Stack spacing={1}>
                {['confirmed', 'shipped', 'delivered', 'cancelled'].filter(s => s !== order.orderStatus).map(s => (
                  <Button key={s} variant="outlined" size="small" fullWidth startIcon={<LocalShippingIcon />}
                    onClick={() => handleStatus(s)}
                    color={s === 'cancelled' ? 'error' : s === 'delivered' ? 'success' : 'info'}
                  >
                    Mark {s.charAt(0).toUpperCase() + s.slice(1)}
                  </Button>
                ))}
                <TextField size="small" label="Tracking Number" value={tracking} onChange={(e) => setTracking(e.target.value)} fullWidth placeholder="Enter tracking ID" />
                <Button variant="contained" size="small" fullWidth onClick={handleSaveTracking} disabled={!tracking.trim() || tracking === order.trackingNumber}>
                  Save Tracking
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetail;
